"use client";

import { useState } from "react";
import { ChevronDown, CheckCircle, Square, CheckSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ContactFormT } from "@/lib/translations";

const WEBHOOK_URL =
  "https://n8n.vrpatirtis.lt/webhook/8a70eee4-5a7d-4be8-9177-0a4ac9a2fe3e";

const ROLES = [
  "CEO / Founder",
  "CTO / VP Engineering",
  "COO / Operations",
  "Head of Marketing",
  "Head of Sales",
  "Product Manager",
  "Other",
];

const COMPANY_SIZES = ["1–10", "11–50", "51–200", "201–500", "500+"];

const REVENUES = [
  "Under $1M",
  "$1M – $5M",
  "$5M – $20M",
  "$20M – $100M",
  "$100M+",
];

const BUDGETS = [
  "Under $10K",
  "$10K – $25K",
  "$25K – $50K",
  "$50K – $100K",
  "$100K+",
];

const EN_INTERESTS = [
  "Lead generation or sales",
  "Customer support",
  "Internal operations",
  "Data processing or reporting",
  "Content or marketing workflows",
  "Not sure yet",
];

const inputCls =
  "border border-black bg-white w-full px-4 py-3 font-body-md text-[15px] text-primary placeholder:text-black/30 focus:outline-none focus:ring-1 focus:ring-black transition-all rounded-none";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-label-caps text-[11px] uppercase tracking-widest text-primary">
        {label}
        {required && <span className="ml-1 opacity-60">*</span>}
      </label>
      {children}
    </div>
  );
}

function SelectField({
  label,
  required,
  placeholder,
  options,
  name,
}: {
  label: string;
  required?: boolean;
  placeholder: string;
  options: string[];
  name: string;
}) {
  return (
    <Field label={label} required={required}>
      <div className="relative">
        <select
          name={name}
          className={`${inputCls} appearance-none pr-10 cursor-pointer`}
          required={required}
          defaultValue=""
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <ChevronDown
          size={18}
          strokeWidth={2}
          className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none icon-gradient"
        />
      </div>
    </Field>
  );
}

export function ContactForm({ t }: { t?: ContactFormT }) {
  const [selected, setSelected] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const interests = t?.interestOptions ?? EN_INTERESTS;

  function toggle(item: string) {
    setSelected((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(false);

    const fd = new FormData(e.currentTarget);
    const payload = {
      firstName: fd.get("firstName") as string,
      lastName: fd.get("lastName") as string,
      email: fd.get("email") as string,
      companyName: fd.get("companyName") as string,
      companyWebsite: fd.get("companyWebsite") as string,
      role: fd.get("role") as string,
      companySize: fd.get("companySize") as string,
      revenue: fd.get("revenue") as string,
      budget: fd.get("budget") as string,
      howCanWeHelp: fd.get("howCanWeHelp") as string,
      improvementAreas: selected,
      submittedAt: new Date().toISOString(),
      source: window.location.href,
    };

    try {
      const res = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Webhook returned non-OK status");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).gtag?.("event", "generate_lead", {
        event_category: "contact_form",
        event_label: payload.companyName,
        value: 1,
      });
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center text-center gap-8 py-24 px-6">
        <div className="w-20 h-20 rounded-full border border-primary bg-white flex items-center justify-center grainy-icon overflow-hidden relative shrink-0">
          <CheckCircle size={36} strokeWidth={1.5} className="icon-gradient relative z-10" />
        </div>
        <div>
          <h3 className="font-headline-lg text-[28px] md:text-[32px] text-primary font-semibold mb-3">
            {t?.successTitle ?? "Message received."}
          </h3>
          <p className="font-body-md text-[15px] text-on-surface-variant font-light max-w-sm">
            {t?.successBody ?? "We got it. Someone on our team will respond within one business day."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-6 md:p-10 flex flex-col gap-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field label={t?.firstNameLabel ?? "First Name"} required>
          <input
            name="firstName"
            className={inputCls}
            type="text"
            placeholder="Jane"
            required
          />
        </Field>
        <Field label={t?.lastNameLabel ?? "Last Name"} required>
          <input
            name="lastName"
            className={inputCls}
            type="text"
            placeholder="Doe"
            required
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field label={t?.emailLabel ?? "Email Address"} required>
          <input
            name="email"
            className={inputCls}
            type="email"
            placeholder="jane@company.com"
            required
          />
        </Field>
        <Field label={t?.companyNameLabel ?? "Company Name"} required>
          <input
            name="companyName"
            className={inputCls}
            type="text"
            placeholder="Acme Inc."
            required
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field label={t?.companyWebsiteLabel ?? "Company Website"}>
          <input
            name="companyWebsite"
            className={inputCls}
            type="url"
            placeholder="https://acme.com"
          />
        </Field>
        <SelectField
          name="role"
          label={t?.roleLabel ?? "Your Role"}
          required
          placeholder={t?.rolePlaceholder ?? "Select your role"}
          options={ROLES}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <SelectField
          name="companySize"
          label={t?.companySizeLabel ?? "Company Size"}
          required
          placeholder={t?.companySizePlaceholder ?? "Select company size"}
          options={COMPANY_SIZES}
        />
        <SelectField
          name="revenue"
          label={t?.revenueLabel ?? "Annual Revenue"}
          required
          placeholder={t?.revenuePlaceholder ?? "Select revenue range"}
          options={REVENUES}
        />
      </div>

      <SelectField
        name="budget"
        label={t?.budgetLabel ?? "Project Budget"}
        required
        placeholder={t?.budgetPlaceholder ?? "Select budget range"}
        options={BUDGETS}
      />

      <Field label={t?.helpLabel ?? "How can we help?"} required>
        <textarea
          name="howCanWeHelp"
          className={`${inputCls} resize-none h-36`}
          placeholder={t?.helpPlaceholder ?? "Describe the process you want automated, or the problem you're trying to solve."}
          required
        />
      </Field>

      <div className="flex flex-col gap-3">
        <span className="font-label-caps text-[11px] uppercase tracking-widest text-primary">
          {t?.interestsLabel ?? "Areas of interest"} <span className="opacity-60">*</span>
        </span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {interests.map((item) => {
            const active = selected.includes(item);
            return (
              <button
                key={item}
                type="button"
                onClick={() => toggle(item)}
                className={`flex items-center gap-3 px-4 py-3 border text-left transition-colors duration-150 ${
                  active
                    ? "border-black bg-black text-white"
                    : "border-black bg-white text-primary hover:bg-surface-container-low"
                }`}
              >
                {active ? (
                  <CheckSquare size={16} strokeWidth={2} className="shrink-0 text-white" />
                ) : (
                  <Square size={16} strokeWidth={1.5} className="shrink-0 icon-gradient" />
                )}
                <span className="font-body-md text-[14px] font-light leading-snug">
                  {item}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <p className="font-body-md text-[14px] text-red-600">
          {t?.errorText ?? "Something went wrong. Please try again or email us at hello@fluxrivet.com."}
        </p>
      )}

      <div className="pt-2 border-t border-black">
        <Button
          className="w-full py-4 font-label-caps text-[12px] tracking-widest uppercase"
          disabled={loading}
        >
          {loading ? (t?.submittingLabel ?? "Sending…") : (t?.submitLabel ?? "Send Message")}
        </Button>
      </div>
    </form>
  );
}
